// :cow: Cosmose CONFIDENTIAL :iso:
import { Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { ElementPosition } from '@med/app/home/demo/guided-tour/services/element-position.service';
import { interval, Subject } from 'rxjs';
import { ElementDimensionObserverAggregateService } from '@med/app/home/demo/guided-tour/services/element-dimension-observer-aggregate.service';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { PortalDimensionsService } from '@med/app/home/demo/guided-tour/services/portal-dimensions.service';
import { StepPortal } from '@med/app/home/demo/guided-tour/models/step';

@Component({
  selector: 'e-portal',
  templateUrl: './portal.component.html',
  styleUrls: ['./portal.component.scss'],
  providers: [ElementDimensionObserverAggregateService],
})
export class PortalComponent implements OnChanges {

  @Input() stepPortal: StepPortal;

  @ViewChild('portal', {static: false}) portal: ElementRef<HTMLElement>;
  @ViewChild('portalTop', {static: false}) portalTop: ElementRef<HTMLElement>;
  @ViewChild('portalLeft', {static: false}) portalLeft: ElementRef<HTMLElement>;
  @ViewChild('portalRight', {static: false}) portalRight: ElementRef<HTMLElement>;
  @ViewChild('portalBottom', {static: false}) portalBottom: ElementRef<HTMLElement>;

  private portalElement: HTMLElement;
  private parentElement: HTMLElement;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private elementPosition: ElementPosition,
    private dimensionObserverService: ElementDimensionObserverAggregateService,
    private portalDimensionsService: PortalDimensionsService,
  ) {
  }

  ngAfterViewInit(): void {
    this.watchDimensionChanges();
    if (this.portal) {
      this.redraw();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.dimensionObserverService.removeAllObservers();
  }

  ngOnChanges(): void {
    if (this.portal) {
      this.redraw();
    }
  }

  private redraw(): void {
    this.dimensionObserverService.unobserve('element');
    this.dimensionObserverService.unobserve('parentElement');

    interval(100).pipe(
      map(() => document.querySelector(this.stepPortal.selector)),
      filter(v => v != null),
      takeUntil(this.unsubscribe$),
      take(1),
    ).subscribe((element: HTMLElement) => {
      this.portalElement = element;
      this.parentElement = document.body.parentElement;
      this.dimensionObserverService.observe('element', this.portalElement);
      this.dimensionObserverService.observe('parentElement', this.parentElement);
      this.showPortal();
    });
  }

  private watchDimensionChanges(): void {
    this.dimensionObserverService.allEvents$.pipe(
      debounceTime(100),
      takeUntil(this.unsubscribe$),
    ).subscribe(() => {
      this.showPortal();
    });
  }

  private showPortal(): void {
    const portalBorderOffset = 2;
    const elementRect = this.portalElement.getBoundingClientRect();
    const dimensions = this.portalDimensionsService.calculatePortalDimensions(elementRect, this.stepPortal.padding);
    const parentElementRect = this.parentElement.getBoundingClientRect();

    this.elementPosition.setElementPosition(
      this.portal.nativeElement,
      dimensions.top - portalBorderOffset - parentElementRect.top,
      dimensions.left - portalBorderOffset,
      dimensions.width + 2 * portalBorderOffset,
      dimensions.height + 2 * portalBorderOffset,
    );
    this.elementPosition.setElementPosition(
      this.portalTop.nativeElement,
      0,
      0,
      parentElementRect.width,
      dimensions.top - parentElementRect.top,
    );
    this.elementPosition.setElementPosition(
      this.portalBottom.nativeElement,
      dimensions.top + dimensions.height - parentElementRect.top,
      0,
      parentElementRect.width,
      parentElementRect.height - dimensions.top - dimensions.height + parentElementRect.top,
    );
    this.elementPosition.setElementPosition(
      this.portalLeft.nativeElement,
      dimensions.top - parentElementRect.top,
      0,
      dimensions.left,
      dimensions.height,
    );
    this.elementPosition.setElementPosition(
      this.portalRight.nativeElement,
      dimensions.top - parentElementRect.top,
      dimensions.left + dimensions.width,
      parentElementRect.width - dimensions.left - dimensions.width,
      dimensions.height,
    );
  }
}
